import React, { useState } from 'react';
import { FileText, CheckCircle, AlertTriangle, TrendingUp, Shield, Clock, Target, Scale, Lightbulb, Users, ChevronDown, Package } from 'lucide-react';
import CTASection from '../../components/CTASection';
import RegistrationModal from '../../components/RegistrationModal';

const PatentApplicationsPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const benefits = [
    {
      icon: Shield,
      title: '20 Years Protection',
      description: 'Secure exclusive rights to your invention for up to 20 years from filing date'
    },
    {
      icon: Target,
      title: 'Broad Coverage',
      description: 'Strategic claim drafting maximizes protection scope and commercial value'
    },
    {
      icon: TrendingUp,
      title: 'Market Advantage',
      description: 'Block competitors and establish market leadership with patent protection'
    },
    {
      icon: CheckCircle,
      title: 'Expert Drafting',
      description: 'Registered patent attorneys with technical backgrounds draft your application'
    }
  ];

  const patentTypes = [
    {
      type: 'Utility Patent',
      description: 'Protects how an invention works, its structure, or composition',
      examples: 'Software, machines, processes, chemical compositions, business methods',
      protection: '20 years from filing date',
      cost: '$8,000 - $15,000'
    },
    {
      type: 'Design Patent',
      description: 'Protects ornamental appearance and visual design of a product',
      examples: 'Product shapes, GUI designs, packaging, jewelry, furniture designs',
      protection: '15 years from grant date',
      cost: '$2,000 - $4,000'
    },
    {
      type: 'Provisional Patent',
      description: 'Temporary 12-month placeholder to establish filing date',
      examples: 'Early-stage inventions, prototypes, inventions still in development',
      protection: '12 months (must convert to utility)',
      cost: '$2,500 - $5,000'
    }
  ];

  const applicationTypes = [
    {
      name: 'Provisional Patent',
      price: '$2,500',
      description: 'Establish priority date while you refine your invention',
      features: ['Patent attorney drafting', 'Technical drawings included', 'Prior art discussion', '12-month protection', 'USPTO filing fees included', '2-week turnaround'],
      bestFor: 'Early-stage inventions, prototypes, inventions being refined'
    },
    {
      name: 'Utility Patent',
      price: '$8,500',
      description: 'Full non-provisional application for utility patents',
      features: ['Registered patent attorney', 'Comprehensive prior art search', 'Strategic claim drafting', 'Detailed specification', 'Professional drawings', 'USPTO filing & prosecution', '4-6 week turnaround'],
      bestFor: 'Fully developed inventions ready for long-term protection'
    },
    {
      name: 'Design Patent',
      price: '$2,000',
      description: 'Protect the ornamental design of your product',
      features: ['Patent attorney preparation', 'Professional patent drawings', 'Design claim crafting', 'Prior art analysis', 'USPTO filing included', '2-3 week turnaround'],
      bestFor: 'Product appearances, GUIs, packaging, ornamental designs'
    }
  ];

  const caseStudies = [
    {
      title: 'Mobile App Patent Generates $2M in Licensing',
      scenario: 'Social media startup invented novel content recommendation algorithm.',
      problem: 'Needed patent protection before showing technology to investors and partners.',
      solution: 'Filed provisional patent to establish priority, then comprehensive utility patent with broad claims covering algorithm variations.',
      outcome: 'Patent granted in 18 months. Licensed technology to major platforms for $2M+ annually.'
    },
    {
      title: 'Medical Device Patent Blocks Competitors',
      scenario: 'Healthcare company developed innovative surgical tool.',
      problem: 'Competitors were preparing to launch similar products.',
      solution: 'Expedited utility patent application with strategic claims covering the core innovation and obvious variations.',
      outcome: 'Patent granted using Track One. Successfully enforced against 2 competitors, maintaining 85% market share.'
    }
  ];

  const timeline = [
    { time: 'Week 1-2', activity: 'Invention disclosure, prior art search, patentability assessment' },
    { time: 'Week 2-4', activity: 'Draft patent specification, claims, and abstract with technical drawings' },
    { time: 'Week 4-5', activity: 'Client review, revisions, and approval of draft application' },
    { time: 'Week 5-6', activity: 'File with USPTO, receive filing date and application number' },
    { time: 'Month 6-18', activity: 'Patent examination, office action responses, and prosecution' },
    { time: 'Month 18-30', activity: 'Patent grant and issuance (average 24 months for utility patents)' }
  ];

  const faqs = [
    {
      question: 'What is the difference between a provisional and utility patent application?',
      answer: 'A provisional patent is a temporary 12-month filing that establishes your priority date but does not get examined. It costs less ($2,500 vs $8,500+) and has relaxed formality requirements. You must file a utility (non-provisional) patent application within 12 months to maintain your priority date. A utility patent is the full application that gets examined by the USPTO and, if approved, grants you enforceable patent rights for 20 years. Most inventors file provisional first to secure priority while refining their invention.'
    },
    {
      question: 'How long does it take to get a patent?',
      answer: 'Utility patents take 18-36 months on average from filing to grant, with the median around 24 months. Design patents are faster at 12-18 months. Factors affecting timing include: patent examiner backlog in your technology area, complexity of your invention, quality of the application, and number of office actions. You can expedite using Track One prioritized examination (12 months) for an additional $4,000 USPTO fee, or qualify for expedited processing if you\'re over 65, have a terminal illness, or the invention relates to green technology.'
    },
    {
      question: 'What can and cannot be patented?',
      answer: 'You CAN patent: useful processes, machines, manufactured articles, compositions of matter, software that improves computer function, business methods tied to technology, and design appearances. You CANNOT patent: abstract ideas, laws of nature, natural phenomena, mathematical formulas, purely mental processes, artistic works (use copyright instead), and inventions that are not novel or are obvious. The invention must be new, non-obvious to someone skilled in the field, and have practical utility.'
    },
    {
      question: 'Do I need a patent attorney or can I file myself?',
      answer: 'While you can legally file yourself (pro se), it is extremely risky. Patent applications are highly technical legal documents with strict requirements. Statistics show pro se applications have: 90%+ rejection rate vs 45% with attorneys, narrower claim scope that provides less protection, higher vulnerability to invalidation challenges, and difficulty enforcing rights. Our patent attorneys know how to: craft claims that overcome prior art, write specifications that provide adequate support, navigate USPTO formalities, and draft claims for maximum protection while meeting legal requirements.'
    },
    {
      question: 'What happens after I file my patent application?',
      answer: 'After filing, you receive a filing date and application number. Your application is published 18 months after filing (unless you opt out for US-only applications). A patent examiner is assigned 8-12 months after filing. The examiner conducts a prior art search and issues a first Office Action (usually a rejection) 12-18 months after filing. You respond to office actions with arguments and amendments. This continues until the examiner allows the application or you abandon it. Once allowed, you pay issue fees and the patent grants 2-3 months later.'
    },
    {
      question: 'How much does a patent cost in total?',
      answer: 'Total costs for a utility patent typically range $10,000-$20,000+ including: patent search ($600-$2,000), patent application drafting and filing ($8,000-$15,000), USPTO filing fees ($1,000-$2,000 depending on entity size), office action responses ($1,500-$3,000 per response, typically 1-3 responses), and maintenance fees over the life of patent ($5,000-$12,000 over 20 years). Design patents are cheaper at $2,000-$5,000 total. Provisional patents cost $2,500-$5,000 initially but must be followed by a utility patent filing.'
    }
  ];

  const processSteps = [
    {
      step: 'Invention Disclosure',
      description: 'Share detailed information about your invention, how it works, and what makes it unique',
      duration: '1-2 days'
    },
    {
      step: 'Prior Art Search',
      description: 'Comprehensive search to assess patentability and identify potential claim strategies',
      duration: '5-7 days'
    },
    {
      step: 'Strategy Session',
      description: 'Discuss search results, patentability, claim scope, and application strategy',
      duration: '1 day'
    },
    {
      step: 'Application Drafting',
      description: 'Patent attorney drafts specification, claims, abstract, and coordinates technical drawings',
      duration: '2-3 weeks'
    },
    {
      step: 'Client Review',
      description: 'Review draft application, provide technical feedback, and approve for filing',
      duration: '3-5 days'
    },
    {
      step: 'USPTO Filing',
      description: 'File application with USPTO and receive filing date and application number',
      duration: '1-2 days'
    },
    {
      step: 'Examination',
      description: 'USPTO examiner reviews application and issues office actions (we respond)',
      duration: '12-24 months'
    },
    {
      step: 'Patent Grant',
      description: 'Pay issue fees and receive patent grant with patent number',
      duration: '2-3 months after allowance'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Patent Application Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Secure Your Innovation with <span className="text-[#168356]">Patent Protection</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Protect your inventions with expert patent applications. Our registered patent attorneys handle everything from provisional patents to full utility patents with strategic claim drafting.
            </p>
            <div className="flex gap-4">
              <button onClick={() => openModal('registration')} className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg transition-all">
                Start Your Application
              </button>
              <button onClick={() => openModal('registration')} className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">
                Free Patent Consultation
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#168356]" />
                Registered Patent Attorneys
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#168356]" />
                4-6 Week Drafting
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#168356]" />
                20 Years Protection
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">20yrs</div>
              <div className="text-gray-600 text-sm">Protection period for utility patents</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">350K+</div>
              <div className="text-gray-600 text-sm">Patents granted annually in US</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">18-24mo</div>
              <div className="text-gray-600 text-sm">Average time to patent grant</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">95%</div>
              <div className="text-gray-600 text-sm">Our patent grant success rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Patent Types */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Types of Patents We File</h2>
            <p className="text-lg text-gray-600">Choose the right patent type for your innovation</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {patentTypes.map((patent, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-black mb-4">{patent.type}</h3>
                <p className="text-gray-700 mb-4">{patent.description}</p>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Examples:</div>
                    <div className="text-gray-600">{patent.examples}</div>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Protection Period:</div>
                    <div className="text-gray-600">{patent.protection}</div>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Typical Cost:</div>
                    <div className="text-[#168356] font-bold">{patent.cost}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Application Services & Pricing */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Patent Application Services</h2>
            <p className="text-lg text-gray-600">Professional patent preparation and filing</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {applicationTypes.map((type, index) => (
              <div key={index} className={`bg-white rounded-xl p-8 shadow-lg border-2 ${index === 1 ? 'border-[#168356] transform scale-105' : 'border-gray-200'}`}>
                {index === 1 && (
                  <div className="bg-[#168356] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2">{type.name}</h3>
                <div className="text-4xl font-bold text-[#168356] mb-4">{type.price}</div>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <div className="text-xs text-gray-500 mb-6 italic">Best for: {type.bestFor}</div>
                <ul className="space-y-3 mb-6">
                  {type.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => openModal('registration')} className={`w-full py-3 rounded-lg font-semibold transition-all ${index === 1 ? 'bg-[#d29b00] text-white hover:bg-[#a87b00]' : 'bg-gray-100 text-black hover:bg-gray-200'}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Why File a Patent?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Patent protection provides exclusive rights and competitive advantages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
              <Lightbulb className="w-12 h-12 text-[#168356] mb-4" />
              <h3 className="text-2xl font-bold text-black mb-4">With Patent Protection</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Exclusive rights:</strong> Prevent others from making, using, or selling your invention</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Licensing revenue:</strong> Generate income by licensing technology to others</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Market dominance:</strong> Block competitors and maintain market leadership</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Investor appeal:</strong> Patents significantly increase company valuation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>Strategic assets:</strong> Use in negotiations, partnerships, and M&A deals</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#168356] font-bold">✓</span>
                  <span><strong>ROI potential:</strong> Patents can generate millions in licensing and enforcement</span>
                </li>
              </ul>
            </div>
            <div className="bg-red-50 rounded-xl p-8 border-l-4 border-red-500">
              <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
              <h3 className="text-2xl font-bold text-black mb-4">Without Patent Protection</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>No exclusivity:</strong> Anyone can copy your invention immediately</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Competitor advantage:</strong> Larger companies can outspend you in market</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Price wars:</strong> Competition drives down margins and profitability</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Lower valuation:</strong> Companies without IP are worth 50-80% less</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Lost opportunities:</strong> Cannot license technology for revenue</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span><strong>Risk of blocking:</strong> Others may patent around you and block YOUR use</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Process Steps */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Patent Application Process</h2>
            <p className="text-lg text-gray-600">From invention to granted patent in 8 clear steps</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <div className="w-12 h-12 bg-[#168356] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                  {index + 1}
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{step.step}</h3>
                <p className="text-sm text-gray-600 mb-3">{step.description}</p>
                <div className="text-xs text-[#168356] font-semibold">{step.duration}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Patent Success Stories</h2>
            <p className="text-lg text-gray-600">See how our patents created value for innovators</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-black mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-500 uppercase mb-2">Scenario</div>
                    <p className="text-gray-700">{study.scenario}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-red-500 uppercase mb-2">Problem</div>
                    <p className="text-gray-700">{study.problem}</p>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#168356] uppercase mb-2">Our Solution</div>
                    <p className="text-gray-700">{study.solution}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm font-semibold text-green-600 uppercase mb-2">Outcome</div>
                    <p className="text-gray-700 font-semibold">{study.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Patent Grant Timeline</h2>
            <p className="text-lg text-gray-600">What to expect from filing to grant</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-[#168356] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-16 bg-[#168356]"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="text-[#168356] font-semibold mb-2">{item.time}</div>
                  <div className="text-gray-700 text-lg">{item.activity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Benefits of Our Patent Service</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#168356] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default PatentApplicationsPage;